<template>
    <Head :title="$t('settings.team') + ' - ' + agency.name" />
    <div class="flex items-center justify-between mb-8">
        <page-title :title="agency.name"></page-title>

        <div v-if="permissions.canDeactivate">
            <span
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="[
                    agency.deactivated === false
                        ? 'bg-lime-100 text-lime-800'
                        : 'bg-gray-100',
                    'text-gray-800',
                ]"
            >
                {{ t(agency.deactivated ? "agencies.inactive" : "agencies.active") }}
            </span>
        </div>

        <div class="flex md:mt-0 md:ml-4">
            <secondary-button
                v-if="permissions.canUpdate"
                @click="$inertia.visitInModal(route(this.links.edit, this.agency.uuid))"
            >
                <PencilIcon
                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                />
                {{ $t("agencies.editAgency") }}
            </secondary-button>

            <primary-button
                v-if="permissions.canAddAdvisors"
                @click="
                    $inertia.visitInModal(route(this.links.addAdvisor, this.agency.uuid))
                "
                class="ml-2"
            >
                <PlusSmIcon
                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                />
                {{ $t("agencies.addTeamMember") }}
            </primary-button>

            <primary-button
                v-if="ownerInvitation"
                @click="resendOwnerInvitation"
                :disabled="inviteSending || inviteSent"
                class="ml-2 relative"
            >
                <!-- Spinner -->
                <svg
                    v-if="inviteSending"
                    class="animate-spin -ml-1 md:mr-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <!-- Checkmark on success -->
                <CheckCircleIcon
                    v-else-if="inviteSent"
                    class="-ml-1 md:mr-2 h-5 w-5 text-white"
                />
                <!-- Default icon -->
                <MailIcon
                    v-else
                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                />
                <span v-if="inviteSending">{{ $t("agencies.sendingInvitation") }}</span>
                <span v-else-if="inviteSent">{{ $t("agencies.invitationSent") }}</span>
                <span v-else>{{ $t("agencies.resendInvitation") }}</span>
            </primary-button>
        </div>
    </div>

    <member-manager
        class="mt-10 sm:mt-0"
        :agency="agency"
        :available-roles="availableRoles"
        :user-permissions="permissions"
        :links="links"
    />

    <!-- BERECHNUNGSART GEBÜHR/HONORAR - SUPERADMIN ONLY
         (Auftrag Luca, 2026-08-16, Entscheide 5 und 6). Die Prop existiert
         nur für Superadmins; für alle anderen Rollen erscheint der Block
         nicht im Markup. -->
    <fee-calculation-mode-card
        v-if="feeCalculation"
        :agency="agency"
        :fee-calculation="feeCalculation"
        :update-link="links.updateFeeCalculationMode"
    />

    <!-- SWITCH TO AGENCY BLOCK - SUPERADMIN ONLY -->
    <div class="mt-10 sm:mt-0" v-if="permissions.canSwitch">
        <div>
            <div class="hidden sm:block">
                <div class="py-8"><div class="border-t border-gray-200"></div></div>
            </div>
            <!-- Manage Team Members -->
            <div class="md:grid md:grid-cols-3 md:gap-6 mt-10 sm:mt-0">
                <div class="md:col-span-1 flex justify-between">
                    <div class="px-4 sm:px-0">
                        <h3 class="text-lg font-medium text-gray-900">
                            Agentur anschauen
                        </h3>
                        <p class="mt-1 text-sm text-gray-600">
                            Wechselt in die Ansicht dieser Agentur. Wichtig:
                            <span class="text-red-500">KEINE</span>
                            Änderungen in diesem Modus vornehmen!
                        </p>
                    </div>
                    <div class="px-4 sm:px-0"></div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="px-4 py-5 sm:p-6 bg-white shadow sm:rounded-lg">
                        <div class="flex space-x-6">
                            <!-- DEACTIVATE -->
                            <secondary-button
                                @click="
                                    $inertia.visit(
                                        route('admin.agencies.switch', this.agency.uuid)
                                    )
                                "
                            >
                                <EyeIcon
                                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                                />
                                {{ $t("agencies.switchAgency") }}
                            </secondary-button>
                            <!-- / DEACTIVATE -->
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- / SWITCH TO AGENCY -->

    <!-- DEACTIVATE DELETE AGENCY -->
    <div class="mt-10 sm:mt-0" v-if="permissions.canDeactivate">
        <div>
            <div class="hidden sm:block">
                <div class="py-8"><div class="border-t border-gray-200"></div></div>
            </div>
            <!-- Manage Agency -->
            <div class="md:grid md:grid-cols-3 md:gap-6 mt-10 sm:mt-0">
                <div class="md:col-span-1 flex justify-between">
                    <div class="px-4 sm:px-0">
                        <h3 class="text-lg font-medium text-gray-900">
                            {{ $t("agencies.deactivateDeleteAgency") }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-600">
                            {{ $t("agencies.deactivateDeleteAgencyDesc") }}
                        </p>
                    </div>
                    <div class="px-4 sm:px-0"></div>
                </div>
                <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="px-4 py-5 sm:p-6 bg-white shadow sm:rounded-lg">
                        <div class="flex space-x-6">
                            <!-- DEACTIVATE -->

                            <secondary-button
                                v-if="!agency.deactivated"
                                @click="
                                    $inertia.visitInModal(
                                        route(
                                            'admin.agencies.deactivateConfirmation',
                                            this.agency.uuid
                                        )
                                    )
                                "
                            >
                                <EyeOffIcon
                                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                                />
                                {{ $t("agencies.deactivateAgency") }}
                            </secondary-button>
                            <secondary-button
                                v-if="agency.deactivated"
                                @click="
                                    $inertia.visitInModal(
                                        route(
                                            'admin.agencies.activateConfirmation',
                                            this.agency.uuid
                                        )
                                    )
                                "
                            >
                                <PencilIcon
                                    class="-ml-1 md:mr-2 h-5 w-5 text-white md:text-minplan-darkgreybeige"
                                />
                                {{ $t("agencies.activateAgency") }}
                            </secondary-button>
                            <!-- / DEACTIVATE -->
                            <!-- DELETE -->
                            <danger-button
                                v-if="permissions.canDelete"
                                :disabled="!deletable"
                                @click="
                                    $inertia.visitInModal(
                                        route(
                                            'admin.agencies.deleteConfirmation',
                                            this.agency.uuid
                                        )
                                    )
                                "
                            >
                                <XCircleIcon class="-ml-1 md:mr-2 h-5 w-5 text-white" />
                                {{ $t("agencies.deleteAgency") }}
                            </danger-button>
                            <!-- / DELETE -->
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <!-- / DEACTIVATE DELETE AGENCY -->
</template>

<script>
import { Head } from "@inertiajs/inertia-vue3";
import PageTitle from "@/Shared/Components/PageTitle";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import DangerButton from "@/Jetstream/DangerButton.vue";
import MemberManager from "@/Pages/Advisor/Agency/Components/MemberManager";
import FeeCalculationModeCard from "@/Pages/Advisor/Agency/Components/FeeCalculationModeCard";
import {
    EyeIcon,
    EyeOffIcon,
    PencilIcon,
    PlusSmIcon,
    XCircleIcon,
    MailIcon,
    CheckCircleIcon,
} from "@heroicons/vue/outline";
import SettingsLayout from "@/Layouts/SettingsLayout";
import { useI18n } from "vue-i18n";

export default {
    components: {
        Head,
        PrimaryButton,
        SecondaryButton,
        DangerButton,
        PageTitle,
        MemberManager,
        FeeCalculationModeCard,
        PencilIcon,
        PlusSmIcon,
        EyeOffIcon,
        EyeIcon,
        XCircleIcon,
        MailIcon,
        CheckCircleIcon,
    },
    props: [
        "agency",
        "availableRoles",
        "permissions",
        "links",
        "deletable",
        // Auftrag 2026-08-16, Entscheid 5: nur für Superadmins geliefert.
        "feeCalculation",
    ],
    layout: [MainLayout, SettingsLayout],
    setup() {
        const { t } = useI18n();
        return { t };
    },
    data() {
        return {
            inviteSending: false,
            inviteSent: false,
        };
    },
    computed: {
        ownerInvitation() {
            return this.agency.invitations.find((i) => i.role === "owner");
        },
    },
    methods: {
        resendOwnerInvitation() {
            if (this.inviteSending || this.inviteSent) return;
            this.inviteSending = true;
            this.inviteSent = false;
            this.$inertia.post(
                this.route(this.links.resendOwnerInvitation, this.agency.uuid),
                {},
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.inviteSending = false;
                        this.inviteSent = true;
                        this.$notify({
                            group: "success",
                            title: "actions.success",
                            text: "agencies.invitationResent",
                        });
                        // Reset back to default state after 3 seconds
                        setTimeout(() => {
                            this.inviteSent = false;
                        }, 3000);
                    },
                    onError: () => {
                        this.inviteSending = false;
                    },
                    onFinish: () => {
                        if (this.inviteSending) {
                            this.inviteSending = false;
                        }
                    },
                }
            );
        },
    },
};
</script>

<style scoped></style>
